// Admin → Webhooks. The read-only diagnostic log over `payment_events`. M7f
// (docs/design-notes/m7f-webhook-log.md).
//
// Lives in the BILLING group, not System: `iconTone.ts` assigns tone per
// DOMAIN, and every row here is a payment event about an invoice about a
// subscription. The workflow is Payments → "why is this invoice still open?" →
// Webhooks, and a cross-group hop would make that two mental steps.
//
// 🔴 NO REPLAY. `recordPaymentEvent` moves money, and its idempotency guard is
// inert exactly where a replay would be safe and absent exactly where it would
// be dangerous. Inspect and copy only — the same posture as the audit log.
//
// English chrome, always LTR. No `@fajrarabi/i18n` import, barrel or deep: the
// barrel's module-load `resolveLanguage()` re-stamps `<html lang/dir>` from the
// `lc_lang` cookie and flipped the whole console RTL once already.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  DataTable,
  DetailRow,
  DetailSection,
  Drawer,
  FilterBar,
  PageHeader,
  Pagination,
  StatusBadge,
  actionsColumn,
  useDebounced,
  useTableState,
  type Column,
  type StatusTone,
} from '~/components';
import {
  flattenPayload,
  getWebhookEvent,
  listWebhookEvents,
  type WebhookEventRow,
} from '~/lib/webhooks';

export const Route = createFileRoute('/_authed/webhooks')({
  component: WebhooksPage,
});

/** Shown until the server has reported the distinct kinds it actually holds. */
const FALLBACK_KINDS = [
  'payment.succeeded',
  'payment.failed',
  'payment.refunded',
  'subscription.cancelled',
];

/**
 * Kind tone is STATUS colour, which `iconTone.ts` explicitly forbids reusing
 * for domain identity — so it stays local to StatusBadge here, exactly as
 * `actionTone()` does in `_authed.audit.tsx`.
 */
function kindTone(kind: string): StatusTone {
  if (kind.endsWith('.succeeded')) return 'success';
  if (kind.endsWith('.failed') || kind.endsWith('.disputed')) return 'danger';
  if (kind.endsWith('.refunded')) return 'warning';
  return 'neutral';
}

function WebhooksPage() {
  const table = useTableState({
    defaultPageSize: 50,
    filterKeys: ['kind', 'provider', 'from', 'to', 'subscriptionId'],
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const q = useDebounced(table.q.trim(), 300);
  const { kind, provider, from, to, subscriptionId } = table.filters;

  const list = useQuery({
    queryKey: [
      'webhooks',
      { q, kind, provider, from, to, subscriptionId, page: table.page, size: table.pageSize },
    ],
    queryFn: ({ signal }) =>
      listWebhookEvents(
        {
          q: q || undefined,
          kind: kind || undefined,
          provider: provider || undefined,
          from: from || undefined,
          to: to || undefined,
          subscriptionId: subscriptionId || undefined,
          limit: table.pageSize,
          offset: (table.page - 1) * table.pageSize,
        },
        signal,
      ),
  });

  // The heavy column crosses the wire only here, on demand, already redacted.
  const detail = useQuery({
    queryKey: ['webhooks', 'detail', openId],
    queryFn: ({ signal }) => getWebhookEvent(openId!, signal),
    enabled: openId != null,
  });

  const kindOptions = (list.data?.kinds.length ? list.data.kinds : FALLBACK_KINDS).map((k) => ({
    value: k,
    label: k,
  }));
  const providerOptions = (list.data?.providers ?? []).map((p) => ({ value: p, label: p }));

  const columns: Column<WebhookEventRow>[] = [
    {
      key: 'receivedAt',
      header: 'Received',
      alwaysVisible: true,
      render: (e) => (
        <span className="whitespace-nowrap text-xs text-admin-500">
          {formatDateTime(e.receivedAt)}
        </span>
      ),
    },
    {
      key: 'eventKind',
      header: 'Kind',
      render: (e) => (
        <StatusBadge status={e.eventKind} label={e.eventKind} tone={kindTone(e.eventKind)} />
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      hideBelow: 'md',
      render: (e) => <span className="text-admin-600">{e.provider}</span>,
    },
    {
      key: 'providerEventId',
      header: 'Event id',
      hideBelow: 'lg',
      render: (e) =>
        e.providerEventId ? (
          <code className="text-xs text-admin-500">{e.providerEventId.slice(0, 12)}</code>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'subscriptionId',
      header: 'Subscription',
      render: (e) =>
        e.subscriptionId ? (
          <button
            type="button"
            className="text-xs text-brand-600 underline-offset-2 hover:underline"
            onClick={(ev) => {
              ev.stopPropagation();
              goToSubscription(e.subscriptionId!);
            }}
          >
            <code>{e.subscriptionId.slice(0, 8)}</code>
          </button>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'summary',
      header: 'Summary',
      hideBelow: 'lg',
      render: (e) => {
        const keys = Object.keys(e.summary);
        if (keys.length === 0) return <span className="text-admin-300">—</span>;
        return (
          <span className="truncate text-xs text-admin-500">
            {keys
              .slice(0, 3)
              .map((k) => `${k}: ${e.summary[k]}`)
              .join(' · ')}
            {keys.length > 3 && ` +${keys.length - 3}`}
          </span>
        );
      },
    },
    // 🔴 Inspect and copy only. There is no destructive action here and no
    // replay, so there is nothing below the divider and no ConfirmDialog.
    actionsColumn<WebhookEventRow>({
      label: (e) => `${e.eventKind} event`,
      primary: (e) => ({
        id: 'open',
        label: 'Open event',
        icon: 'eye',
        onSelect: () => openEvent(e.id),
      }),
      actions: (e) => [
        {
          id: 'copy-event-id',
          label: 'Copy event id',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(e.providerEventId ?? ''),
          disabled: !e.providerEventId,
          disabledReason: 'This event carried no provider event id.',
        },
        {
          id: 'copy-payload',
          label: 'Copy payload JSON',
          icon: 'copy',
          onSelect: () => {
            if (detail.data && detail.data.id === e.id) {
              void navigator.clipboard?.writeText(JSON.stringify(detail.data.payload, null, 2));
            }
          },
          disabled: !detail.data || detail.data.id !== e.id,
          disabledReason: 'Open the event first.',
        },
        {
          id: 'filter-kind',
          label: 'Filter by this kind',
          icon: 'filter',
          onSelect: () => table.setFilter('kind', e.eventKind),
        },
        {
          id: 'view-subscription',
          label: 'View subscription',
          icon: 'external',
          onSelect: () => {
            if (e.subscriptionId) goToSubscription(e.subscriptionId);
          },
          disabled: !e.subscriptionId,
          disabledReason: 'This event is not linked to a subscription.',
        },
      ],
    }),
  ];

  function openEvent(id: string) {
    setShowRaw(false);
    setOpenId(id);
  }

  const rows = list.data?.rows ?? [];
  const flattened =
    detail.data && !detail.data.truncated ? flattenPayload(detail.data.payload) : {};

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Webhooks"
        description="Provider payment events, exactly as they arrived. Read-only — newest first."
      />

      {subscriptionId && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-2 rounded-full bg-admin-100 px-3 py-1 text-admin-700">
            Filtered to subscription <code className="text-xs">{subscriptionId.slice(0, 8)}</code>
            <button
              type="button"
              className="text-admin-400 hover:text-admin-700"
              onClick={() => table.setFilter('subscriptionId', null)}
              aria-label="Clear subscription filter"
            >
              ×
            </button>
          </span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(e) => e.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(e) => openEvent(e.id)}
        isRowActive={(e) => e.id === openId}
        columnVisibility
        skeletonRows={10}
        caption="Webhook events"
        empty={{
          icon: 'webhook',
          title: table.isFiltered ? 'No events match these filters' : 'No webhook events yet',
          description: table.isFiltered
            ? 'Try a different kind, provider or date range.'
            : 'Provider callbacks are recorded here as they arrive. An empty log means the endpoint has not been called.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Find by provider event id (e.g. evt_abc)"
            filters={[
              { key: 'kind', label: 'Kind', value: kind ?? '', options: kindOptions },
              ...(providerOptions.length > 0
                ? [
                    {
                      key: 'provider',
                      label: 'Provider',
                      value: provider ?? '',
                      options: providerOptions,
                    },
                  ]
                : []),
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          >
            <label className="flex items-center gap-1.5 text-xs text-admin-500">
              From
              <input
                type="date"
                value={from ?? ''}
                onChange={(e) => table.setFilter('from', e.target.value || null)}
                className="rounded-lg border border-admin-200 px-2 py-1.5 text-sm text-admin-700"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-admin-500">
              To
              <input
                type="date"
                value={to ?? ''}
                onChange={(e) => table.setFilter('to', e.target.value || null)}
                className="rounded-lg border border-admin-200 px-2 py-1.5 text-sm text-admin-700"
              />
            </label>
          </FilterBar>
        }
        footer={
          <Pagination
            page={table.page}
            pageSize={table.pageSize}
            total={list.data?.total ?? 0}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      />

      <Drawer
        open={openId != null}
        onOpenChange={(open) => !open && setOpenId(null)}
        title="Webhook event"
        description={detail.data ? formatDateTime(detail.data.receivedAt) : undefined}
        width="lg"
        badge={
          detail.data && (
            <StatusBadge
              status={detail.data.eventKind}
              label={detail.data.eventKind}
              tone={kindTone(detail.data.eventKind)}
            />
          )
        }
      >
        {detail.isLoading && <p className="py-4 text-sm text-admin-400">Loading event…</p>}
        {detail.data && (
          <>
            <DetailSection title="Event">
              <DetailRow label="Kind">
                <code className="text-xs">{detail.data.eventKind}</code>
              </DetailRow>
              <DetailRow label="Provider">{detail.data.provider}</DetailRow>
              <DetailRow label="Provider event id">
                {detail.data.providerEventId ? (
                  <code className="break-all text-xs">{detail.data.providerEventId}</code>
                ) : (
                  <span className="text-admin-400">none — this event carried no id</span>
                )}
              </DetailRow>
              <DetailRow label="Received at">{formatDateTime(detail.data.receivedAt)}</DetailRow>
              <DetailRow label="Internal id">
                <code className="break-all text-xs">{detail.data.id}</code>
              </DetailRow>
            </DetailSection>

            <DetailSection title="Linked">
              <DetailRow label="Subscription">
                {detail.data.subscriptionId ? (
                  <button
                    type="button"
                    className="break-all text-brand-600 underline-offset-2 hover:underline"
                    onClick={() => goToSubscription(detail.data.subscriptionId!)}
                  >
                    <code className="text-xs">{detail.data.subscriptionId}</code>
                  </button>
                ) : (
                  // SET NULL + the writer resolving it from the invoice means
                  // null legitimately reads "no invoice matched" — information,
                  // not an error.
                  <span className="text-admin-400">not linked — no invoice matched this event</span>
                )}
              </DetailRow>
            </DetailSection>

            <DetailSection title="Payload">
              <p className="pb-2 text-xs text-admin-400">
                Secrets are redacted server-side. There is no unredacted view.
              </p>
              {detail.data.truncated ? (
                <div className="py-2 text-sm text-admin-500">
                  <p>This payload is too large to display.</p>
                  <p className="mt-1 text-xs text-admin-400">
                    Top-level keys: {detail.data.payloadKeys.join(', ') || '—'}
                  </p>
                </div>
              ) : (
                <>
                  {Object.entries(flattened).map(([key, value]) => (
                    <DetailRow key={key} label={key}>
                      <span className="break-all">{value}</span>
                    </DetailRow>
                  ))}
                  <div className="pt-2">
                    <button
                      type="button"
                      className="text-xs text-admin-500 underline-offset-2 hover:underline"
                      onClick={() => setShowRaw((v) => !v)}
                    >
                      {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
                    </button>
                    {showRaw && (
                      <pre className="mt-2 overflow-x-auto rounded-lg bg-admin-50 p-3 text-xs text-admin-700">
                        {JSON.stringify(detail.data.payload, null, 2)}
                      </pre>
                    )}
                  </div>
                </>
              )}
            </DetailSection>
          </>
        )}
      </Drawer>
    </div>
  );
}

/**
 * 🔴 ASSIGN the URL, do not `navigate({ search })`. The list routes declare no
 * `validateSearch`, and `useTableState` reads `window.location.search` — a
 * router-level search update would not reach it. Documented M7c trap.
 */
function goToSubscription(subscriptionId: string) {
  window.location.assign(`/subscriptions?q=${encodeURIComponent(subscriptionId)}`);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
